import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import ActivityCard from '../components/activities/ActivityCard';
import ActivityStats from '../components/activities/ActivityStats';
import axios from '../utils/axios';
import { useAuth } from '../context/AuthContext';

const MyActivities = () => {
  const { t } = useTranslation();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user, setUser } = useAuth();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchActivities();
  }, [user, navigate]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/activities/user/registered');
      setActivities(response.data);
      setError(null);
    } catch (error) {
      setError(t('errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRegistration = async () => {
    if (!selectedActivity) return;

    try {
      await axios.delete(`/api/activities/${selectedActivity._id}/register`);
      enqueueSnackbar(t('registrationCancelSuccess'), { variant: 'success' });
      fetchActivities(); // Refresh the list
    } catch (error) {
      const msg = error.response?.data?.message || t('errorCancelling');
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      handleCloseDialog();
    }
  };

  const handleOpenDialog = (activity) => {
    setSelectedActivity(activity);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedActivity(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleModifyParticipants = async (activityId, newNumberOfParticipants, useSubscription) => {
    try {
      const response = await axios.put(`/api/activities/${activityId}/participants`, {
        newNumberOfParticipants,
        useSubscription
      });

      // Update the activity in the list
      setActivities(activities.map(activity => {
        if (activity._id === activityId) {
          return {
            ...activity,
            numberOfParticipants: newNumberOfParticipants,
            currentParticipants: response.data.currentParticipants
          };
        }
        return activity;
      }));

      // Update user's remaining activities if needed
      if (response.data.remainingActivities !== undefined) {
        setUser(prev => ({
          ...prev,
          remainingActivities: response.data.remainingActivities
        }));
      }

      enqueueSnackbar(t('participantsModifiedSuccess'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || t('errorModifyingParticipants'), { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Mes activités
      </Typography>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Liste des activités" />
        <Tab label="Statistiques" />
      </Tabs>

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : activities.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Vous n'êtes pas encore inscrit à des activités.
        </Alert>
      ) : (
        <>
          {activeTab === 0 ? (
            <>
              {/* Upcoming Activities */}
              <Box sx={{ mb: 6 }}>
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                  Prochaines activités
                </Typography>
                <Grid container spacing={3}>
                  {activities
                    .filter(activity => new Date(activity.date) >= new Date())
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((activity) => (
                      <Grid item xs={12} sm={6} md={4} key={activity._id}>
                        <ActivityCard
                          activity={activity}
                          onCancelRegistration={() => handleOpenDialog(activity)}
                          onModifyParticipants={handleModifyParticipants}
                          numberOfParticipants={activity.numberOfParticipants}
                          showCancelButton={true}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Box>

              {/* Past Activities */}
              <Box>
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                  Activités passées
                </Typography>
                <Grid container spacing={3}>
                  {activities
                    .filter(activity => new Date(activity.date) < new Date())
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((activity) => (
                      <Grid item xs={12} sm={6} md={4} key={activity._id}>
                        <ActivityCard
                          activity={activity}
                          onCancelRegistration={() => handleOpenDialog(activity)}
                          onModifyParticipants={handleModifyParticipants}
                          numberOfParticipants={activity.numberOfParticipants}
                          isPastActivity
                          showCancelButton={true}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Box>
            </>
          ) : (
            <ActivityStats activities={activities} />
          )}
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{t('cancelRegistration')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('confirmCancelRegistration', { activity: selectedActivity?.title })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('keepRegistration')}</Button>
          <Button onClick={handleCancelRegistration} color="error" autoFocus>
            {t('confirmCancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyActivities; 